import axios from "axios"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"

import { Link as RouterLink } from "react-router-dom"
import FormControl from "@material-ui/core/FormControl"
import TextField from "@material-ui/core/TextField"
import Button from "@material-ui/core/Button"
import Typography from "@material-ui/core/Typography"
import Link from "@material-ui/core/Link"
import Box from "@material-ui/core/Box"
import InputAdornment from "@material-ui/core/InputAdornment"
import AccountCircleIcon from "@material-ui/icons/AccountCircle"

import { AxiosHelper } from "common/helpers"
import { AuthApi } from "common/api"
import { AuthLayout } from "./AuthLayout"
import { Alert } from "components/widgets"

const ForgotPassword = () => {
    const [email, setEmail] = useState(null)

    function onSubmitted(emailValue) {
        setEmail(emailValue)
    }

    if (email !== null) {
        return <SuccessForm email={email} />
    }

    return <Form onSubmitted={onSubmitted} />
}

const Form = props => {
    const { t } = useTranslation()
    const [email, setEmail] = useState("")
    const [error, setError] = useState(null)
    const [isLoading, setIsLoading] = useState(false)

    const [fontSize, setFontSize] = useState(window.innerWidth * 0.015)

    useEffect(() => {
        const handleResize = () => {
            setFontSize(window.innerWidth * 0.015);
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        }
    }, [])


    const styleButton = {
        backgroundColor: "black",
        color: 'white',
        fontWeight: "bold",
        fontSize: fontSize * 0.75
    }

    async function onSubmit(event) {
        event.preventDefault()
        setIsLoading(true)

        try {
            await AuthApi.requestPasswordReset(email)
            props.onSubmitted(email)
        } catch (error) {
            // must be in catch, because when the request was successful this form is gone and the call would throw an error
            setIsLoading(false)

            if (axios.isAxiosError(error)) {
                const parsed = AxiosHelper.parseError(error)
                setError(typeof parsed === "string" ? { general: parsed } : parsed)
            } else {
                setError({ general: t("errors.somethingWentWrong") })
            }
        }
    }

    function onChange(event) {
        setEmail(event.target.value)
    }

    return (
        <AuthLayout>
            {error?.general && (
                <Alert marginBottom={2} severity="error">
                    {error?.general}
                </Alert>
            )}

            <Typography style={{ color: "white" }} >
                {t("view.forgotPassword.info")}
            </Typography>

            <Box width="100%" component="form" onSubmit={onSubmit}>
                <FormControl fullWidth margin="normal">
                    <TextField
                        required
                        label={t("view.login.emailLabel")}
                        type="email"
                        onChange={onChange}
                        autoComplete="username"
                        error={Boolean(error?.email)}
                        helperText={error?.email}
                        InputLabelProps={{ style: { color: "white", fontSize } }}
                        InputProps={{
                            style: {
                                color: "white",
                                fontWeight: "bold",
                                fontSize,
                                borderBottom: "1px solid white"
                            },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <AccountCircleIcon />
                                </InputAdornment>
                            )
                        }}
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <Button
                        style={styleButton}
                        fullWidth
                        disabled={isLoading}
                        color="primary"
                        variant="contained"
                        type="submit"
                    >
                        {isLoading
                            ? t("view.forgotPassword.submitButtonLoading")
                            : t("view.forgotPassword.submitButton")}
                    </Button>
                </FormControl>
            </Box>

            <Typography style={{ color: "white" }} className="w-full flex justify-center items-center">
                {t("view.forgotPassword.rememberPassword")}&nbsp;
                <Link style={{ color: "white" }} component={RouterLink} to="/login">
                    {t("general.login")}
                </Link>
            </Typography>
        </AuthLayout>
    )
}

const SuccessForm = props => {
    const { email } = props
    const { t } = useTranslation()
    const [isLoading, setIsLoading] = useState(false)
    const [isResent, setIsResent] = useState(false)

    const styleButton = {
        backgroundColor: "black",
        color: 'white',
        fontWeight: "bold",
    }

    async function onClickResend() {
        if (isLoading) return
        if (!email.trim().length) return

        setIsLoading(true)
        setIsResent(false)

        try {
            await AuthApi.requestPasswordReset(email)
            setIsResent(true)
        } catch (error) {
            console.warn(error)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <AuthLayout>
            {isResent && (
                <Alert severity="success">
                    {t("view.forgotPassword.success.emailResentInfo")}
                </Alert>
            )}

            <FormControl fullWidth margin="normal">
                <Typography style={{ color: "white" }} component="h2" variant="h6">
                    {t("view.forgotPassword.success.title")}
                </Typography>

                <Typography style={{ color: "white" }}>
                    {t("view.forgotPassword.success.info", { email })}
                </Typography>
            </FormControl>

            <FormControl fullWidth margin="normal">
                <Button style={styleButton}
                    fullWidth
                    component={RouterLink}
                    to="/login"
                    color="primary"
                    variant="contained"
                >
                    <div className="text-white">{t("general.login")}</div>
                </Button>
            </FormControl>

            <Typography style={{ color: "white" }}>
                {t("view.forgotPassword.success.didntReceiveEmail")}&nbsp;
                <Link style={{ color: "white" }} disabled={isLoading} component="button" onClick={onClickResend}>
                    {isLoading
                        ? t("view.forgotPassword.success.resendLinkLoading")
                        : t("view.forgotPassword.success.resendLink")}
                </Link>
            </Typography>
        </AuthLayout>
    )
}

export default ForgotPassword
